import {IStateCommon} from './reducer';
import {Page} from '../constant/page';
import {
  IUserAgent,
  IFetchStatus,
  IToast,
  INoticeType,
  INotice,
  IUserCashInfo,
  IWebToAppBanner,
} from '../type';
import {PAlert} from '../type/alert';
import {getFetchKey} from '../util/fetch/fetchUtil';
import {
  createAction,
  createBasicActions,
  createActionFetch,
} from '../util/redux';

export enum ActionType {
  SetValue = 'common.SetValue',
  SetPropertyValues = 'common.SetPropertyValues',
  AddPAlert = 'common.AddPAlert',
  RemovePAlert = 'common.RemovePAlert',
  RemoveAllPAlerts = 'common.RemoveAllPAlerts',
  RemoveAllPAlertsForRouting = 'common.RemoveAllPAlertsForRouting',
  ChangePAlertConfirmState = 'common.ChangePAlertConfirmState',
  AddToast = 'common.AddToast',
  RemoveToast = 'common.RemoveToast',
  SetFetchStatus = 'common.SetFetchStatus',
  SetIsSlow = 'common.SetIsSlow',
  SetUserAgent = 'common.SetUserAgent',
  SetIsAppMounted = 'common.SetIsAppMounted',
  SetIsFirstFetched = 'common.SetIsFirstFetched',
  FetchLastUpdatedTime = 'common.FetchLastUpdatedTime',
  RemoveFetchError = 'common.RemoveFetchError',
  SetIsHideAppDownloadBanners = 'common.SetIsHideAppDownloadBanners',
  SetFullLoading = 'common.SetFullLoading',
  FetchUserCashInfo = 'common.FetchUserCashInfo',
  FetchNoticeList = 'common.FetchNoticeList',
  AddNoticeList = 'common.AddNoticeList',
  FetchSetContentLike = 'common.FetchSetContentLike',
  SetContentLike = 'common.SetContentLike',
  SetContentLikes = 'common.SetContentLikes',
  SetValuesWhenRoutingFinished = 'common.SetValuesWhenRoutingFinished',
  SetUserCashInfo = 'common.SetUserCashInfo',
  FetchWebToAppBanner = 'common.FetchWebToAppBanner',
  SetWebToAppBanner = 'common.SetWebToAppBanner',
  DecreaseTotalCount = 'common.DecreaseTotalCount',
  RemoveFetchStatus = 'common.RemoveFetchStatus',
}

export const actions = {
  ...createBasicActions<IStateCommon>({
    setValueActionType: ActionType.SetValue,
    setPropertyValuesActionType: ActionType.SetPropertyValues,
  }),
  addAlert: (data: PAlert) =>
    createAction(ActionType.AddPAlert, {
      data,
    }),
  removeAlert: () => createAction(ActionType.RemovePAlert),
  removeAllAlerts: () => createAction(ActionType.RemoveAllPAlerts),
  removeAllAlertsForRouting: () =>
    createAction(ActionType.RemoveAllPAlertsForRouting),
  // TODO: confirm state(diable/enable) 이외에도 범용적으로 사용할 수 있도록 수정하자
  changePAlertConfirmState: (payload: {groupId: string; disabled: boolean}) =>
    createAction(ActionType.ChangePAlertConfirmState, payload),
  addToast: (data: IToast) => createAction(ActionType.AddToast, {data}),
  removeToast: () => createAction(ActionType.RemoveToast),
  setFetchStatus: (params: ISetFetchStatusParam) =>
    createAction(ActionType.SetFetchStatus, getParamsWithFetchKey(params)),
  setIsSlow: (params: {
    actionType: string;
    fetchKey?: string | number;
    isSlow: boolean;
  }) => createAction(ActionType.SetIsSlow, getParamsWithFetchKey(params)),
  setIsFirstFetched: (params: {
    actionType: string;
    fetchKey?: string | number;
    isFirstFetched: boolean;
  }) =>
    createAction(ActionType.SetIsFirstFetched, getParamsWithFetchKey(params)),
  setUserAgent: (userAgent?: IUserAgent) =>
    createAction(ActionType.SetUserAgent, {userAgent}),
  setIsAppMounted: (isAppMounted: boolean) =>
    createAction(ActionType.SetIsAppMounted, {isAppMounted}),
  fetchLastUpdatedTime: () =>
    createActionFetch(ActionType.FetchLastUpdatedTime),
  removeFetchError: (params: {
    actionType: string;
    fetchKey?: string | number;
  }) =>
    createAction(ActionType.RemoveFetchError, getParamsWithFetchKey(params)),
  setIsHideAppDownloadBanners: (page: Page, value: boolean) =>
    createAction(ActionType.SetIsHideAppDownloadBanners, {
      page,
      value,
    }),
  setFullLoading: (isShow: boolean) =>
    createAction(ActionType.SetFullLoading, {isShow}),
  fetchUserCashInfo: () => createActionFetch(ActionType.FetchUserCashInfo),
  fetchNoticeList: (
    payload: {
      id?: number;
      type: INoticeType;
    },
    isFirst?: boolean
  ) =>
    createActionFetch(ActionType.FetchNoticeList, {
      payload,
      fetchKey: payload.type,
      isFirst,
    }),
  addNoticeList: (noticeList: INotice[], isFirst?: boolean) =>
    createAction(ActionType.AddNoticeList, {noticeList, isFirst}),
  fetchSetContentLike: (payload: {id: number; isLike: boolean}) =>
    createActionFetch(ActionType.FetchSetContentLike, {
      payload,
    }),
  setContentLike: (payload: {id: number; isLike: boolean}) =>
    createAction(ActionType.SetContentLike, payload),
  setContentLikes: (likeIds: number[] = [], isLike: boolean) =>
    createAction(ActionType.SetContentLikes, {likeIds, isLike}),
  setValuesWhenRoutingFinished: () =>
    createAction(ActionType.SetValuesWhenRoutingFinished),
  setUserCashInfo: (userCashInfo: IUserCashInfo, totalCash: number) =>
    createAction(ActionType.SetUserCashInfo, {userCashInfo, totalCash}),
  fetchWebToAppBanner: (isViewerPage: boolean) =>
    createActionFetch(ActionType.FetchWebToAppBanner, {
      payload: {isViewerPage},
      fetchKey: getWebToAppBannerFetchkey(isViewerPage),
    }),
  setWebToAppBanner: (banner: IWebToAppBanner, isViewerPage: boolean) =>
    createAction(ActionType.SetWebToAppBanner, {banner, isViewerPage}),
  decreaseTotalCount: (actionType: string, fetchKey?: string | number) =>
    createAction(ActionType.DecreaseTotalCount, {
      actionType,
      fetchKey: getFetchKey({type: actionType, fetchKey}),
    }),
  removeFetchStatus: (
    actionList: Array<{actionType: string; fetchKey?: string | number}>
  ) => createAction(ActionType.RemoveFetchStatus, {actionList}),
};

function getParamsWithFetchKey<
  P extends {
    actionType: string;
    fetchKey?: string | number;
  },
>(params: P) {
  return {
    ...params,
    fetchKey: getFetchKey({
      type: params.actionType,
      fetchKey: params.fetchKey,
    }),
  };
}

export interface ISetFetchStatusParam {
  actionType: string;
  fetchKey?: string | number;
  status: IFetchStatus;
  isFirstFetch: boolean;
  totalCount?: number;
  nextPaging?: string | number;
  isEnd?: boolean;
  resultCode?: number;
  resultMessage?: string;
}

export function getWebToAppBannerFetchkey(isViewerPage: boolean) {
  return isViewerPage ? 'viewer' : 'normal';
}
